import { AxiosInstance } from 'axios';
import { removeUndefinedFromObject } from 'utils/common';
import { TApiResponse } from '@type/common';
import { ICourseTagProperties } from '@type/tag.type';
import {
  CreateSeriesTagRequest,
  UpdateSeriesTagRequest,
  TagPartialResponse,
} from 'features/tag/types/api';
import {
  CreateResourceTagRequest,
  UpdateResourceTagRequest,
  IStyleTagParams,
  ITagResponseStructure,
  UpdateExamTagParams,
  UpdateHomeworkTagParams,
  GetSeriesTagParams,
  SeriesTagDto,
  GetExamTagParams,
  GetResourceTagParams,
  GetHomeworkTagParams,
  GetCourseTagParams,
  CourseTagDTO,
  ResourceTagDto,
  ExamTagDto,
  StyleTagDto,
  HomeworkTagDto,
  getTagByCodeResponseMap,
  GetSeriesSubjectParams,
  GetSeriesSubjectResponse,
  GetSeriesCategoryParams,
  GetSeriesCategoryResponse,
  GetSeriesVersionCodeParams,
  GetSeriesVersionCodeResponse,
} from './type';

export default (axios: AxiosInstance, tag: string) => ({
  /**
   * 取得 課程 tag
   */
  getCourseTag(
    data?: GetCourseTagParams,
  ): TApiResponse<ITagResponseStructure<CourseTagDTO>> {
    return axios.get(
      `${tag}/tag/course`,
      data && { params: removeUndefinedFromObject(data) },
    );
  },

  /**
   * 新增 叢書 tag
   */
  createSeriesTag(
    data: CreateSeriesTagRequest,
  ): TApiResponse<TagPartialResponse> {
    return axios.post(`${tag}/tag/series`, data);
  },
  /**
   * 取得 叢書 tag
   */
  getSeriesTag(
    data: GetSeriesTagParams,
  ): TApiResponse<ITagResponseStructure<SeriesTagDto>> {
    return axios.get(`${tag}/tag/series`, { params: data });
  },
  /**
   * 取得 叢書科目
   */
  getSeriesSubject(
    data: GetSeriesSubjectParams,
  ): TApiResponse<GetSeriesSubjectResponse> {
    return axios.get(`${tag}/tag/series-subject`, { params: data });
  },
  /**
   * 取得 叢書分類
   */
  getSeriesCategory(
    data: GetSeriesCategoryParams,
  ): TApiResponse<GetSeriesCategoryResponse> {
    return axios.get(`${tag}/tag/series-category`, { params: data });
  },
  /**
   * 取得 叢書版本
   */
  getSeriesVersionCode(
    data: GetSeriesVersionCodeParams,
  ): TApiResponse<GetSeriesVersionCodeResponse> {
    return axios.get(`${tag}/tag/series-version-code`, { params: data });
  },
  /**
   * 修改 叢書 tag
   */
  updateSeriesTag(
    id: string,
    data: UpdateSeriesTagRequest,
  ): TApiResponse<TagPartialResponse> {
    return axios.put(`${tag}/tag/series/${id}`, data);
  },
  /**
   * 新增 叢書教材 tag
   */
  createResourceTag(data: CreateResourceTagRequest): TApiResponse<any> {
    return axios.post(`${tag}/tag/resource`, data);
  },
  /**
   * 取得 叢書教材 tag
   */
  getResourceTag(
    data: GetResourceTagParams,
  ): TApiResponse<ITagResponseStructure<ResourceTagDto>> {
    return axios.get(`${tag}/tag/resource`, { params: data });
  },
  /**
   * 修改 叢書教材 tag
   */
  updateResourceTag(
    id: string,
    data: UpdateResourceTagRequest,
  ): TApiResponse<TagPartialResponse> {
    return axios.put(`${tag}/tag/resource/${id}`, data);
  },
  /**
   * 取得 tag 資料 by code
   */
  getTagByCode<T extends keyof getTagByCodeResponseMap>(
    type: T,
    code: string,
  ): TApiResponse<getTagByCodeResponseMap[T]> {
    return axios.get(`${tag}/tag/${type}/${code}`);
  },
  /**
   * 取得 作業 tag
   */
  getHomeworkTag(
    data: GetHomeworkTagParams,
  ): TApiResponse<ITagResponseStructure<HomeworkTagDto>> {
    return axios.get(`${tag}/tag/homework`, { params: data });
  },
  /**
   * 修改 作業 tag
   */
  updateHomeworkTag(
    id: string,
    data: UpdateHomeworkTagParams,
  ): TApiResponse<TagPartialResponse> {
    return axios.put(`${tag}/tag/homework/${id}`, data);
  },
  /**
   * 取得 測驗 tag
   */
  getExamTag(
    data: GetExamTagParams,
  ): TApiResponse<ITagResponseStructure<ExamTagDto>> {
    return axios.get(`${tag}/tag/exam`, { params: data });
  },
  /**
   * 修改 測驗 tag
   */
  updateExamTag(
    id: string,
    data: UpdateExamTagParams,
  ): TApiResponse<TagPartialResponse> {
    return axios.put(`${tag}/tag/exam/${id}`, data);
  },
  /**
   * 上傳 tag
   */
  uploadTag(type: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    return axios.post(`${tag}/tag/import/${type}`, form, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  },
  /**
   * 匯出 tag
   */
  async exportCourseTag(type: string): TApiResponse<any> {
    return axios.get(`${tag}/tag/export/${type}`);
  },
  /**
   * 刪除 tag (封存)
   */
  deleteTag(type: string, id: string): TApiResponse<any> {
    return axios.delete(`${tag}/tag/${type}/${id}`);
  },
  /**
   * 取得教學風格 tag
   */
  getStyleTag(
    data?: IStyleTagParams,
  ): TApiResponse<ITagResponseStructure<StyleTagDto>> {
    return axios.get(`${tag}/tag/style`, { params: data });
  },
  /**
   * 取得篩選 tag
   */
  getTagMeta(): TApiResponse<ICourseTagProperties[]> {
    return axios.get(`${tag}/tag/meta`);
  },
  /**
   * 重新排序 教材/測驗/作業
   */
  sortTag(
    type: string,
    data: {
      tags: string[];
      seriesCode: string;
    },
  ): TApiResponse<any> {
    return axios.patch(`${tag}/tag/${type}/sort`, data);
  },
  /**
   * 批次下架
   */
  batchTakeOffTag(
    type: string,
    data: {
      tags: string[];
      isDeleted: boolean;
    },
  ): TApiResponse<any> {
    return axios.post(`${tag}/tag/${type}/available`, data);
  },
});
